import type { VideoPlayerInterface } from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { AviaPlayerHooksManager } from 'tag/ts/tag/player/adapters/avia/hooks/AviaPlayerHooksManager';
import { IAviaPlayerAdapterAPI } from 'tag/ts/tag/player/adapters/avia/interfaces/adapter/IAviaPlayerAdapterAPI';
import { IAviaPlayerWrapper } from 'tag/ts/tag/player/adapters/avia/interfaces/adapter/IAviaPlayerWrapper';
import { IAviaPlayerWrapperArgument } from 'tag/ts/tag/player/adapters/avia/interfaces/arguments/IAviaPlayerWrapperArgument';
import { AviaAPITransformer } from 'tag/ts/tag/player/adapters/avia/transformers/AviaAPITransformer';
import { AviaEventTransformer } from 'tag/ts/tag/player/adapters/avia/transformers/AviaEventTransformer';
import { TTagEventCallback } from 'tag/ts/tag/types/TTagEventCallback';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description VTG Avia player wrapper providing Smart Tag-compatible interface
 *
 * **Wrapper Pattern Implementation:**
 * Wraps VTG Avia player instances to provide a Smart Tag-compatible API surface
 * while maintaining access to VTG's full functionality. Acts as the integration
 * layer between VTG player instances and Smart Tag components.
 *
 * **Integration Component Coordination:**
 * - **AviaAPITransformer**: Translates VTG APIs to Smart Tag player interface
 * - **AviaEventTransformer**: Converts VTG events to Smart Tag event system
 * - **AviaPlayerHooksManager**: Manages VTG hook system for behavior customization
 * - **VTG Player Instance**: Direct reference to wrapped VTG player
 *
 * **API Surface Management:**
 * Provides unified API access through the `API` getter that returns AviaAPITransformer,
 * ensuring Smart Tag components interact with standardized player interface rather
 * than VTG-specific APIs directly.
 *
 * **Lifecycle Coordination:**
 * - **Initialization**: Sets up transformers and hook managers with VTG player
 * - **Event Setup**: Configures VTG event listeners through transformer integration
 * - **State Management**: Tracks wrapper state to prevent race conditions during cleanup
 * - **Cleanup**: Coordinates proper teardown of all integration components
 *
 * **Race Condition Prevention:**
 * Implements `#isDestroyed` flag to prevent async operations from running after
 * wrapper destruction, ensuring clean separation between VTG and Smart Tag during teardown.
 *
 * **Business Impact:**
 * Enables Smart Tag to use VTG player technology through a stable, Smart Tag-compatible
 * interface that isolates Smart Tag from VTG implementation changes.
 */
export class AviaPlayerWrapper extends AbstractTagClass implements IAviaPlayerWrapper {

	// placeholder for adapted avia player instance
	#adaptedPlayerInstance: VideoPlayerInterface;
	// placeholder for avia player API transformer class instance
	#aviaAPITransformer: IAviaPlayerAdapterAPI;
	// placeholder for avia event transformer class instance
	#aviaEventTransformer: AviaEventTransformer;
	// placeholder for avia player hooks manager class instance
	#aviaPlayerHooksManager: AviaPlayerHooksManager;
	// Add a cleanup flag
	#isDestroyed: boolean = false;
	// promise that resolves when event listeners are ready
	#eventListenersReady: Promise<IPromiseResolveGeneric>;

	constructor( argument: IAviaPlayerWrapperArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerWrapper'
			}
		);

		// store adapted avia payer instance
		this.#adaptedPlayerInstance = argument.aviaPlayerInstance;
		// create and store avia event transformer class instance
		this.#aviaEventTransformer = new AviaEventTransformer( { tag: argument?.tag, aviaPlayerWrapper: this } );
		// create and store avia API transformer class instance
		this.#aviaAPITransformer = new AviaAPITransformer( { tag: argument?.tag, aviaPlayerWrapper: this, aviaEventTransformer: this.#aviaEventTransformer } );
		// create and store all player hook configuration instances
		this.#aviaPlayerHooksManager = new AviaPlayerHooksManager( { tag: argument?.tag, player: argument.aviaPlayerInstance } );

		// Check destroyed state before starting async operations to prevent race condition
		if ( !this.#isDestroyed ) {
			this.#eventListenersReady = this.#setupEventListeners();
		}
	}

	/**
	 * @description Sets up event listeners and returns a promise that resolves when ready
	 * @private
	 * @returns {Promise<IPromiseResolveGeneric>} Promise that resolves when event listeners are registered
	 */
	#setupEventListeners = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				// subscribe to avia player events
				this.#aviaAPITransformer.addPlayerEventListener().then(
					( data ) => {
						// Double-check we're still not destroyed after async operation
						if ( !this.#isDestroyed ) {
							// subscribe to Tag application events
							this.tagEventsHandler(
								this.#onTagEvent,
								[
									TagEvent.ON_TAG_PLAYER_STOP_REQUEST
								]
							);
							resolve( { msg: 'Event listeners registered', success: true } );
						} else {
							reject( { msg: 'Wrapper destroyed during setup', success: false } );
						}
					}
				).catch(
					( error ) => {
						// Only log if not destroyed (expected failure during cleanup)
						if ( !this.#isDestroyed ) {
							this?.logger?.warn( 'Error setting up event listeners:', error );
						}
						reject( { msg: 'Failed to register event listeners', success: false, error } );
					}
				);
			}
		);
	};

	/**
	 * @description Waits for event listeners to be registered before allowing player operations
	 * @returns {Promise<IPromiseResolveGeneric>} Promise that resolves when wrapper is ready
	 */
	waitForReady = (): Promise<IPromiseResolveGeneric> => {
		return this.#eventListenersReady;
	};

	/**
	 * @description Returns instance of adapted avia player
	 * @returns {VideoPlayerInterface}
	 */
	get adaptedPlayerInstance() {
		return this.#adaptedPlayerInstance;
	}

	/**
	 * @description Sets avia player instance
	 * @param {VideoPlayerInterface} value
	 */
	set adaptedPlayerInstance( value: VideoPlayerInterface ) {
		this.#adaptedPlayerInstance = value;
	}

	/**
	 * @description Returns instance of adapted player API
	 * @returns {IAviaPlayerAdapterAPI}
	 */
	get API(): IAviaPlayerAdapterAPI {
		return this.#aviaAPITransformer;
	}

	/**
	 * @description Cleanup method for garbage collection routines
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	cleanUp = (): Promise<IPromiseResolveGeneric> => {
		// Set destroyed flag early
		this.#isDestroyed = true;

		// aux method for unsubscribing from Tag events
		const unsubscribeFromTagEvents = (): Promise<IPromiseResolveGeneric> => {
			return new Promise(
				( resolve, reject ) => {
					/**
					 * Unsubscribe ONLY from the specific events this wrapper subscribed to.
					 *
					 * **BUG FIX (2025-10-02):**
					 * Previously, this called `removeTagEventsHandler()` with `tagEvents()` which returns
					 * ALL TagEvent types. This caused the wrapper to unsubscribe from ALL events (including
					 * events it never subscribed to), inadvertently removing OTHER components' subscriptions
					 * like ErrorManager's ON_FATAL_ERROR listener.
					 *
					 * **THE FIX:**
					 * Only unsubscribe from `ON_TAG_PLAYER_STOP_REQUEST` - the only event this wrapper
					 * actually subscribes to. This prevents accidentally removing other components' listeners.
					 */
					this.removeTagEventsHandler( this.#onTagEvent, this.tag.eventDispatcher, [ TagEvent.ON_TAG_PLAYER_STOP_REQUEST ] ).then(
						( data ) => {
							resolve( { msg: 'Unsubscribed from tag events.', success: true } );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'Error unsubscribing from tag events:', error );
							resolve( { msg: 'Tag events cleanup failed', success: false } );
						}
					);
				}
			);
		};

		return new Promise(
			( resolve, reject ) => {
				const cleanupHooksAndFinalize = () => {
					const cleaningUpHooks = this.#aviaPlayerHooksManager?.cleanUp?.();

					if ( this.utils.Type.isNullOrUndefined( cleaningUpHooks ) ) {
						this?.logger?.warn(
							'hook cleanUp has already run or unavailable',
							'this.#aviaPlayerHooksManager',
							this.#aviaPlayerHooksManager
						);
						this.#finalizeCleanup( unsubscribeFromTagEvents, resolve );
						return;
					}

					// remove all player hooks
					cleaningUpHooks.then(
						( data ) => {
							this.#aviaPlayerHooksManager = undefined;
							this.#finalizeCleanup( unsubscribeFromTagEvents, resolve );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'Error cleaning up hooks:', error );
							this.#aviaPlayerHooksManager = undefined;
							this.#finalizeCleanup( unsubscribeFromTagEvents, resolve );
						}
					);
				};

				if ( this.utils.Type.isFunction( this.#aviaAPITransformer?.removePlayerEventListener ) ) {
					this.#aviaAPITransformer.removePlayerEventListener().then(
						( data ) => {
							cleanupHooksAndFinalize();
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'Error removing player event listeners:', error );
							// Continue with cleanup even if event listener removal failed
							cleanupHooksAndFinalize();
						}
					);
				} else {
					cleanupHooksAndFinalize();
				}
			}
		);
	};

	/**
	 * @description Finalizes cleanup by clearing references and unsubscribing from tag events
	 * @param {() => Promise<IPromiseResolveGeneric>} unsubscribeFromTagEvents
	 * @param {(result: { msg: string; success: boolean }) => void} resolve
	 */
	#finalizeCleanup( unsubscribeFromTagEvents: () => Promise<IPromiseResolveGeneric>, resolve: ( result: { msg: string; success: boolean } ) => void ): void {
		this.#adaptedPlayerInstance = undefined;
		this.#aviaAPITransformer = undefined;
		this.#aviaEventTransformer = undefined;

		// unsubscribe from all tag events
		unsubscribeFromTagEvents().then(
			( data ) => {
				resolve( { msg: 'Cleanup done.', success: true } );
			}
		).catch(
			( error ) => {
				this?.logger?.warn( 'Error unsubscribing from tag events:', error );
				resolve( { msg: 'Cleanup done with warnings.', success: true } );
			}
		);
	}

	/**
	 * @description Player stop request handler
	 * @param {TagEventVO} event
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	requestPlayerStop = ( event: TagEventVO = undefined ): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				/**
				 * @description Callback we may call is
				 * @param {boolean} success
				 * @param result
				 */
				const executeCallback: TTagEventCallback = ( success: boolean, result: any ) => {
					// if player stop result callback exists
					if ( this.utils.Type.isFunction( event?.data?.eventCallback ) && this.utils.Type.isBoolean( success ) ) {
						this.utils.Delay.delay()
							.then(
								() => {
									this?.logger?.log( 'requestPlayerStop:', 'execute event callback:', success, result );
									// execute it
									event?.data?.eventCallback( success, result );
								}
							).catch(
								( error ) => {
									this?.logger?.warn( 'Error executing callback with delay:', error );
								// Execute callback directly if delay fails
									event?.data?.eventCallback( success, result );
								}
							);
					}
				};

				// if player exists
				if ( this.utils.Type.isDefined( this?.API ) ) {
					// Store API reference to avoid race condition
					const apiRef = this.API;

					// inform application we are about to stop the player
					this.dispatchTagEvent(
						new TagEventVO(
							TagEvent.ON_TAG_BEFORE_PLAYER_STOP_REQUEST,
							this.tag,
							undefined,
							this
						)
					);

					// Double-check API is still available and stop player
					if ( apiRef && this.utils.Type.isFunction( apiRef?.stop ) ) {
						apiRef.stop().then(
							( result ) => {
								this?.logger?.log( 'requestPlayerStop:', 'Success' );
								// inform application
								this.dispatchTagEvent(
									new TagEventVO(
										TagEvent.ON_TAG_PLAYER_STOP_SUCCESS,
										this.tag,
										undefined,
										this
									)
								);
								executeCallback( true, result );
								resolve( { msg: 'done', result: result } );
							}
						).catch(
							( error ) => {
								this?.logger?.warn( 'requestPlayerStop:', 'Failure' );
								// inform application
								this.dispatchTagEvent(
									new TagEventVO(
										TagEvent.ON_TAG_PLAYER_STOP_FAILURE,
										this.tag,
										undefined,
										this
									)
								);
								executeCallback( false, error );
								resolve( { msg: 'error', result: error } );
							}
						);
					} else {
						this?.logger?.warn( 'requestPlayerStop:', 'API became unavailable during operation' );
						executeCallback( false, 'API unavailable' );
						resolve( { msg: 'error', result: 'API unavailable' } );
					}
				}
			}
		);
	};

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_TAG_PLAYER_STOP_REQUEST:
				if ( !this.utils.Type.isEmpty( this?.adaptedPlayerInstance ) ) {
					this.requestPlayerStop( event ).then(
						() => {
							// Success - no action needed
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'Player stop request failed:', error );
						}
					);
				}
				break;
		}
	};
}